/**
 * Rows the server has turned down, and what to do with them until the
 * operator changes something (CU-869ffwj7a).
 *
 * The save loop Thibault filmed: a 409 on a price limit, the error branch
 * reloads, the reload makes `items` a new array, the save effect sees it
 * and sends the same rejected payload again. For ever. The fix is to
 * **remember** what was refused — by row id and by the fingerprint of the
 * attempt — and to keep it out of the next sends until its fingerprint
 * changes. Guessing the server's rules in the browser is not an option:
 * they moved once already (`reject_duplicate_price_limit` compares the
 * ACRISS of two matrices, which no client predicate can see).
 *
 * Pure module, unit-tested with `node --test`; the hook only wires it.
 */

/**
 * An entry of the held map: `{ fingerprint, message, status, kind }`,
 * `kind` being `refused` (a 4xx: the server judged THIS row) or
 * `stalled` (5xx, network, timeout, no status: the server did not
 * answer, which says nothing about the row).
 */
export function kindOf(error) {
	const s = error?.status
	return Number.isInteger(s) && s >= 400 && s < 500 ? 'refused' : 'stalled'
}

/** Held, and still exactly what was turned down. */
export function isRefused(row, held, fingerprint) {
	const entry = held?.get(row?.id)
	return !!entry && entry.fingerprint === fingerprint(row)
}

/**
 * What goes to the server, given what is on screen.
 *
 * Holding a row is **substitution, not omission** (TL, plan §1.0): the
 * diff reads "in `prev`, not in `next`" as a DELETE, so leaving out a row
 * the server already has would erase it two clicks after a 409. A held
 * row the server knows travels as the server's own version — no update,
 * no delete; a held row the server has never seen is simply not created.
 * Both kinds of entry are substituted, `refused` and `stalled` alike,
 * and before — and regardless of — `readyToSave`, which a collection may
 * not define at all (Category consistency).
 *
 * Returns `{ toSend, held }`, `held` being every id kept back for either
 * reason — the set the chip reads.
 */
export function holdBack(rows, { server = [], held = new Map(), fingerprint, readyToSave = null }) {
	const onServer = new Map(server.map(x => [x.id, x]))
	const kept = new Set()
	const toSend = []
	for (const row of rows) {
		if (isRefused(row, held, fingerprint)) {
			kept.add(row.id)
			const theirs = onServer.get(row.id)
			if (theirs) toSend.push(theirs)
			continue
		}
		if (!onServer.has(row.id) && readyToSave && !readyToSave(row, rows)) {
			kept.add(row.id)
			continue
		}
		toSend.push(row)
	}
	return { toSend, held: kept }
}

/**
 * A `stalled` row is sent again with the operator's next edit — and only
 * then. The cycle the error branch's own reload fires must not release
 * it, or a 502 would loop exactly like the 409 did: `next` coming from
 * that reload is the very array the reload stored, so identity tells the
 * two apart. Returns the map without its stalled entries, or the same map
 * when nothing is released.
 */
export function releaseStalled(held, next, lastReload) {
	if (next === lastReload) return held
	const out = new Map([...held].filter(([, e]) => e.kind !== 'stalled'))
	return out.size === held.size ? held : out
}

/**
 * After a cycle that went through: every entry whose row travelled as
 * the operator's version — not as the substitute — is cleared. `kept` is
 * the set `holdBack` returned for that cycle, computed from the ids the
 * rows had **before** the remap, which is what the map is keyed on.
 */
export function releaseSaved(held, kept) {
	const out = new Map([...held].filter(([id]) => kept.has(id)))
	return out.size === held.size ? held : out
}
